import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
  tokens,
} from '@coreai/fluentui-react';
import { useServiceProviderConnector } from '@/hooks/useServiceProviderConnector';
import { useTelemetry } from '@/hooks/useTelemetry';
import { buildConnectorManifest } from '@/services/manifestConnectionService';
import type { ConnectionReference } from '@/types/connectionReference';
import type { McpConnection } from '@/types/logicApps';
import { AgentProviderChooser } from './AgentProviderChooser';
import { ConnectionCreationContent } from './ConnectionCreationContent';
import { ConnectionFamilyChooser } from './ConnectionFamilyChooser';
import { ConnectorChooser } from './ConnectorChooser';
import type {
  AgentProviderChoice,
  ConnectionCategory,
  ConnectorChoice,
  CreationFamily,
} from './addConnectionTypes';
import { useConnectionCatalog } from './useConnectionCatalog.hooks';
import type { ConnectionsEditorState } from './useConnectionsEditor';

const useStyles = makeStyles({
  surface: {
    width: 'min(760px, calc(100vw - 32px))',
    maxWidth: '760px',
    maxHeight: 'calc(100vh - 32px)',
  },
  connectorListSurface: {
    height: 'min(640px, calc(100vh - 32px))',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minHeight: 0,
    overflowY: 'auto',
  },
  connectorListContent: {
    height: '100%',
    overflow: 'hidden',
  },
  bodyWithoutActions: {
    '&::before': {
      display: 'none',
    },
  },
  connectorListBody: {
    height: '100%',
    minHeight: 0,
  },
});

export interface AddConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  state: ConnectionsEditorState;
}

export const AddConnectionDialog = ({
  open,
  onClose,
  state,
}: AddConnectionDialogProps) => {
  const styles = useStyles();
  const { trackEvent, trackException } = useTelemetry();
  const [category, setCategory] = useState<ConnectionCategory | null>(null);
  const [selectedConnector, setSelectedConnector] =
    useState<ConnectorChoice | null>(null);
  const [selectedAgentProvider, setSelectedAgentProvider] =
    useState<AgentProviderChoice | null>(null);
  const [search, setSearch] = useState('');
  const family: CreationFamily | null =
    selectedConnector?.family ??
    (category === 'agent' || category === 'mcp' ? category : null);
  const catalog = useConnectionCatalog(
    search,
    state.projectName,
    state.appName,
  );
  const serviceProviderConnectorQuery = useServiceProviderConnector(
    family === 'serviceProvider' ? selectedConnector?.id ?? null : null,
  );

  useEffect(() => {
    if (!open) return;
    setCategory(null);
    setSelectedConnector(null);
    setSelectedAgentProvider(null);
    setSearch('');
  }, [open]);

  const existingConnectionNames = useMemo(
    () => [
      ...(state.connections ?? []).map((connection) => connection.name),
      ...Object.keys(state.serviceProviderConnections ?? {}),
      ...Object.keys(state.agentConnections ?? {}),
      ...Object.keys(state.mcpConnections ?? {}),
    ],
    [
      state.agentConnections,
      state.connections,
      state.mcpConnections,
      state.serviceProviderConnections,
    ],
  );

  const serviceProviderManifest = useMemo(
    () =>
      buildConnectorManifest({
        connectorId:
          family === 'serviceProvider' ? selectedConnector?.id : undefined,
        serviceProviderConnector: serviceProviderConnectorQuery.data,
        operationManifest: null,
      }),
    [family, selectedConnector?.id, serviceProviderConnectorQuery.data],
  );

  const selectCategory = (nextCategory: ConnectionCategory) => {
    setCategory(nextCategory);
    setSelectedConnector(null);
    setSelectedAgentProvider(null);
    setSearch('');
    trackEvent('connections.creation.family.selected', {
      family: nextCategory,
    });
  };

  const selectConnector = (connector: ConnectorChoice) => {
    setSelectedConnector(connector);
    trackEvent('connections.creation.connector.selected', {
      family: connector.family,
    });
  };

  const selectAgentProvider = (provider: AgentProviderChoice) => {
    setSelectedAgentProvider(provider);
    trackEvent('connections.creation.connector.selected', {
      family: 'agent',
      provider: provider.type,
    });
  };

  const finalizeReference = async (
    referenceKey: string,
    reference: ConnectionReference,
  ) => {
    try {
      await state.handleCreateConnectionReference(referenceKey, reference);
      trackEvent('connections.creation.succeeded', { family });
    } catch (error) {
      trackEvent('connections.creation.failed', { family });
      trackException(error, {
        context: 'connections.creation.finalize',
        family,
      });
      throw error;
    }
  };

  const completeReference = () => {
    if (family) state.setSelectedTab(family);
    onClose();
  };

  const saveMcp = async (recordKey: string, record: McpConnection) => {
    try {
      await state.handleCreateLocalConnection(recordKey, 'mcp', record);
      trackEvent('connections.creation.succeeded', { family: 'mcp' });
    } catch (error) {
      trackEvent('connections.creation.failed', { family: 'mcp' });
      trackException(error, {
        context: 'connections.creation.finalize',
        family: 'mcp',
      });
      throw error;
    }
  };

  const goBack = () => {
    if (selectedConnector) {
      setSelectedConnector(null);
      return;
    }
    if (selectedAgentProvider) {
      setSelectedAgentProvider(null);
      return;
    }
    setCategory(null);
    setSearch('');
  };

  let content: ReactNode = (
    <ConnectionFamilyChooser onSelect={selectCategory} />
  );
  if (category === 'connectors' && !selectedConnector) {
    content = (
      <ConnectorChooser
        connectors={catalog.connectors}
        search={search}
        isLoading={catalog.isLoading}
        error={catalog.error}
        partialError={catalog.partialError}
        onBack={goBack}
        onSearchChange={setSearch}
        onRetry={catalog.retry}
        onSelect={selectConnector}
      />
    );
  } else if (category === 'agent' && !selectedAgentProvider) {
    content = (
      <AgentProviderChooser
        onBack={goBack}
        onSelect={selectAgentProvider}
      />
    );
  } else if (family) {
    content = (
      <ConnectionCreationContent
        family={family}
        selectedConnector={selectedConnector}
        selectedAgentProvider={selectedAgentProvider}
        serviceProviderManifest={serviceProviderManifest}
        isServiceProviderLoading={serviceProviderConnectorQuery.isLoading}
        existingConnectionNames={existingConnectionNames}
        existingMcpConnectionKeys={Object.keys(state.mcpConnections ?? {})}
        isDraftMode={state.isDraftMode}
        onFinalize={finalizeReference}
        onComplete={completeReference}
        onBack={goBack}
        onClose={onClose}
        onRetryServiceProvider={() =>
          void serviceProviderConnectorQuery.refetch()
        }
        onSaveMcp={saveMcp}
        onMcpAdded={() => {
          state.setSelectedTab('mcp');
          onClose();
        }}
      />
    );
  }

  const isConnectorListOpen =
    category === 'connectors' && selectedConnector === null;
  const showDialogActions =
    category === null ||
    (category === 'agent' && selectedAgentProvider === null);

  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_, data) => {
        const creationFormOpen =
          !!selectedConnector ||
          !!selectedAgentProvider ||
          category === 'mcp';
        if (!data.open && !creationFormOpen) onClose();
      }}
    >
      <DialogSurface
        className={mergeClasses(
          styles.surface,
          isConnectorListOpen ? styles.connectorListSurface : undefined,
        )}
      >
        <DialogBody
          className={mergeClasses(
            showDialogActions ? undefined : styles.bodyWithoutActions,
            isConnectorListOpen ? styles.connectorListBody : undefined,
          )}
        >
          <DialogTitle>Add connection</DialogTitle>
          <DialogContent
            className={mergeClasses(
              styles.content,
              isConnectorListOpen
                ? styles.connectorListContent
                : undefined,
            )}
          >
            {content}
          </DialogContent>
          {showDialogActions && (
            <DialogActions>
              <Button appearance="secondary" onClick={onClose}>
                Cancel
              </Button>
            </DialogActions>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

AddConnectionDialog.displayName = 'AddConnectionDialog';
